<template>
    <HeaderContent title="Route" />
    <ActionContent
        ctaText="Route"
        :filterDate="false"
        :filterModal="false"
        @action="createModal"
        @search-query="searchData"
    />
    <DestinationForm
        :modal="modal"
        :selectedData="selectedData"
        @update:modal="updateModal"
        @addOrUpdate="getAll"
    ></DestinationForm>

    <DataTable
        :data="routeList"
        :header="headers"
        :custom-actions="customActions"
    >
    </DataTable>
</template>

<script setup>
import axios from "axios";
import HeaderContent from "@/components/HeaderContent.vue";
import ActionContent from "@/components/ActionContent.vue";
import DestinationForm from "./RouteForm.vue";
import DataTable from "@/components/DataTable.vue";
import { ref, onMounted, inject } from "vue";
import { useLoading } from "vue-loading-overlay";
import { useAuthStore } from "@/store/auth.js";
import { useToast } from "vue-toastification";
import { formatDecimal } from "@/utils/format";

const auth = useAuthStore();
const $loading = useLoading();
const selectedData = ref({});
const modal = ref(false);
const swal = inject("$swal");
const toast = useToast();

const headers = ref([
    { key: "no", label: "No", thClass: "text-center px-1", tdClass: "text-center px-1" },
    {
        key: "from",
        label: "From",
        sortable: true,
        thClass: "text-left",
        tdClass: "text-left",
    },
    {
        key: "to",
        label: "To",
        sortable: true,
        thClass: "text-left",
        tdClass: "text-left",
    },
    { key: "distance", label: "Distance", sortable: true },
    { key: "duration", label: "Duration", sortable: true },
        {
        key: "description",
        label: "Description",
        sortable: true,
        thClass: "text-left",
        tdClass: "text-left text-wrap",
        formatter: (value) => {
            if (!value) return "-";
            return value.length > 100 ? value.substring(0, 100) + "..." : value;
        },
    },
    { key: "action", label: "Action" },
]);
const customActions = ref([
    {
        label: "Edit",
        variant: "success",
        icon: "fas fa-edit",
        action: (item) => {
            getByUuid(item.uuid);
        },
    },
    {
        label: "Delete",
        variant: "danger",
        icon: "fas fa-trash",
        action: (item) => {
            swal({
                title: "Confirm",
                text: "Are you sure to delete this data?",
                showCancelButton: true,
                confirmButtonText: "Yes, delete!",
                cancelButtonText: "Cancel",
                customClass: {
                    popup: "rounded",
                    confirmButton: "btn btn-danger me-2",
                    cancelButton: "btn btn-secondary",
                },
                buttonsStyling: false,
            }).then((confirm) => {
                if (confirm.value) {
                    deleteData(item.uuid);
                } else {
                    swal.close();
                }
            });
        },
    },
]);

const searchData = async (search) => {
    const loader = $loading.show({
        color: "#489464",
        loader: "spinner",
    });
    try {
        await getAll(search);
    } finally {
        loader.hide();
    }
};

const createModal = () => {
    selectedData.value = {};
    modal.value = true;
};

const updateModal = (newValue) => {
    modal.value = newValue;
};

let routeList = ref([]);

const getAll = async (search = "") => {
    const response = await axios.get(`${process.env.VUE_APP_API_URL}/route`, {
        params: {
            search: search,
        },
        headers: {
            "Content-type": "application/json",
            Authorization: "Bearer " + auth._token,
        },
    });

    const res = response.data;

    if (res.status) {
        routeList.value = res.data.map((i) => ({
            ...i,
            from: i.from.name + " (" + i.from.code + ")",
            to: i.to.name + " (" + i.to.code + ")",
            distance: i.distance ? formatDecimal(i.distance) + " KM" : "-",
            duration: i.duration
                ? formatDecimal(i.duration) + " " + i.duration_unit
                : "-",
        }));
    }
};

const getByUuid = async (uuid) => {
    if (uuid) {
        toast.clear();
        const loader = $loading.show({
            color: "#009689",
            loader: "bars",
            zIndex: 999999,
        });

        try {
            const response = await axios.get(
                `${process.env.VUE_APP_API_URL}/route/detail/${uuid}`,
                {
                    headers: {
                        "Content-type": "application/json",
                        Authorization: "Bearer " + auth._token,
                    },
                }
            );

            const res = response.data;

            if (res.status) {
                selectedData.value = res.data;
                modal.value = true;
            }
        } finally {
            loader.hide();
        }
    }
};

const deleteData = async (uuid) => {
    if (uuid) {
        toast.clear();
        const loader = $loading.show({
            color: "#009689",
            loader: "bars",
            zIndex: 999999,
        });

        try {
            const response = await axios.delete(
                `${process.env.VUE_APP_API_URL}/route/delete/${uuid}`,
                {
                    headers: {
                        "Content-type": "application/json",
                        Authorization: "Bearer " + auth._token,
                    },
                }
            );

            const res = response.data;

            if (res.status) {
                toast.success(res.message);
                getAll();
            }
        } finally {
            loader.hide();
        }
    }
};

onMounted(async () => {
    const loader = $loading.show({
        color: "#489464",
        loader: "spinner",
    });
    try {
        await getAll();
    } finally {
        loader.hide();
    }
});
</script>
